import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { StepHandle, StepProps } from '../BusinessProfileSetupPage';
import { businessProfileSetupGateway } from '../../services/BusinessProfileSetupGateway';
import { monthDayOptions, WEEKDAY_OPTIONS } from '../../validation';
import { ApiError } from '../../../identity/types';
import {
  PAY_DAY_KIND,
  PAY_FREQUENCY,
  type CreatePayScheduleRequest,
  type PayDayKind,
  type PayFrequency,
  type SavePayScheduleDraftRequest,
} from '../../types';

const FREQUENCY_OPTIONS: { value: PayFrequency; label: string }[] = [
  { value: PAY_FREQUENCY.Weekly, label: 'Weekly' },
  { value: PAY_FREQUENCY.BiWeekly, label: 'Biweekly' },
  { value: PAY_FREQUENCY.SemiMonthly, label: 'Twice a Month' },
  { value: PAY_FREQUENCY.Monthly, label: 'Monthly' },
  { value: PAY_FREQUENCY.Quarterly, label: 'Quarterly' },
];

interface FormState {
  frequency: PayFrequency | 0;
  payPeriodStartDate: string;
  /** Weekday (1-5) for Weekly/BiWeekly, "1".."28" or "last" for SemiMonthly/Monthly. Unused for Quarterly. */
  payDay: string;
  /** SemiMonthly only: "16".."28" or "last". */
  secondDay: string;
  /** Quarterly only. */
  firstPayDate: string;
}

const EMPTY: FormState = { frequency: 0, payPeriodStartDate: '', payDay: '', secondDay: '', firstPayDate: '' };

/** Common shape of both PayScheduleResponse (canonical) and PayScheduleDraftResponse (Step 4's own
 * module-owned draft) — used to prefill the form from either source with the same mapping logic. */
interface SchedulePayFields {
  frequency: PayFrequency | null;
  firstPayDay: number | null;
  firstPayDayKind: PayDayKind | null;
  secondPayDay: number | null;
  secondPayDayKind: PayDayKind | null;
  payPeriodStartDate: string | null;
  firstPayDate: string | null;
}

function formFromSchedule(schedule: SchedulePayFields): FormState {
  const frequency = schedule.frequency ?? 0;
  const isSemi = frequency === PAY_FREQUENCY.SemiMonthly;
  const isMonthly = frequency === PAY_FREQUENCY.Monthly;
  const isWeekly = frequency === PAY_FREQUENCY.Weekly || frequency === PAY_FREQUENCY.BiWeekly;
  return {
    frequency,
    payPeriodStartDate: schedule.payPeriodStartDate ?? '',
    payDay: isWeekly
      ? String(schedule.firstPayDay ?? '')
      : isSemi || isMonthly
        ? schedule.firstPayDayKind === PAY_DAY_KIND.LastDayOfMonth
          ? 'last'
          : String(schedule.firstPayDay ?? '')
        : '',
    secondDay: isSemi
      ? schedule.secondPayDayKind === PAY_DAY_KIND.LastDayOfMonth
        ? 'last'
        : String(schedule.secondPayDay ?? '')
      : '',
    firstPayDate: schedule.firstPayDate ?? '',
  };
}

export const PayrollSetupStep = forwardRef<StepHandle, StepProps>(function PayrollSetupStep({ markStepComplete, readOnly }, ref) {
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // EPN-37 unsaved-changes correction: see BasicInfoStep's own identical comment on this pattern.
  const baselineRef = useRef<FormState>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existing = await businessProfileSetupGateway.getActivePaySchedule();
        if (cancelled) return;
        if (existing) {
          const loaded = formFromSchedule(existing);
          setForm(loaded);
          baselineRef.current = loaded;
          return;
        }
        // No canonical pay schedule yet — restore Step 4's own module-owned draft, if any.
        const draft = await businessProfileSetupGateway.getPayScheduleDraft();
        if (!cancelled && draft) {
          const loaded = formFromSchedule(draft);
          setForm(loaded);
          baselineRef.current = loaded;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isWeekly = form.frequency === PAY_FREQUENCY.Weekly || form.frequency === PAY_FREQUENCY.BiWeekly;
  const isSemi = form.frequency === PAY_FREQUENCY.SemiMonthly;
  const isMonthly = form.frequency === PAY_FREQUENCY.Monthly;
  const isQuarterly = form.frequency === PAY_FREQUENCY.Quarterly;

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!form.frequency) errs.frequency = 'Select a pay schedule.';
    if (!form.payPeriodStartDate) errs.payPeriodStartDate = 'Select a valid pay period start date.';
    if (!isQuarterly && !form.payDay) errs.payDay = 'Select a pay day.';
    if (isSemi && !form.secondDay) errs.secondDay = 'Select the second pay day for a twice-monthly schedule.';
    if (isQuarterly) {
      if (!form.firstPayDate) {
        errs.firstPayDate = 'Select a valid first pay date on or after the pay period start date.';
      } else if (form.payPeriodStartDate && form.firstPayDate < form.payPeriodStartDate) {
        errs.firstPayDate = 'First Pay Date cannot be before the Pay Period Start Date.';
      }
    }
    return errs;
  };

  const toRequest = (): CreatePayScheduleRequest => {
    const base = { name: 'Company Pay Schedule', payPeriodStartDate: form.payPeriodStartDate || null };
    if (isWeekly) {
      return { ...base, frequency: form.frequency as PayFrequency, firstPayDay: Number(form.payDay), firstPayDayKind: PAY_DAY_KIND.DayOfMonth };
    }
    if (isSemi) {
      return {
        ...base,
        frequency: form.frequency as PayFrequency,
        firstPayDay: form.payDay === 'last' ? null : Number(form.payDay),
        firstPayDayKind: PAY_DAY_KIND.DayOfMonth,
        secondPayDay: form.secondDay === 'last' ? null : Number(form.secondDay),
        secondPayDayKind: form.secondDay === 'last' ? PAY_DAY_KIND.LastDayOfMonth : PAY_DAY_KIND.DayOfMonth,
      };
    }
    if (isMonthly) {
      return {
        ...base,
        frequency: form.frequency as PayFrequency,
        firstPayDay: form.payDay === 'last' ? null : Number(form.payDay),
        firstPayDayKind: form.payDay === 'last' ? PAY_DAY_KIND.LastDayOfMonth : PAY_DAY_KIND.DayOfMonth,
      };
    }
    // Quarterly.
    return { ...base, frequency: form.frequency as PayFrequency, firstPayDay: null, firstPayDate: form.firstPayDate };
  };

  const saveIfValid = async (): Promise<boolean> => {
    if (Object.keys(validate()).length > 0) return false;
    await businessProfileSetupGateway.savePaySchedule(toRequest());
    return true;
  };

  /** Whatever is currently filled in, mapped into Step 4's own module-owned draft shape — every field is
   * optional here, unlike toRequest(), which assumes a fully valid, submittable schedule. None of this
   * data is sensitive. */
  const toDraftRequest = (): SavePayScheduleDraftRequest => {
    let firstPayDay: number | null = null;
    let firstPayDayKind: PayDayKind | null = null;
    let secondPayDay: number | null = null;
    let secondPayDayKind: PayDayKind | null = null;
    let firstPayDate: string | null = null;

    if (isWeekly) {
      firstPayDay = form.payDay ? Number(form.payDay) : null;
      firstPayDayKind = PAY_DAY_KIND.DayOfMonth;
    } else if (isSemi) {
      firstPayDay = form.payDay && form.payDay !== 'last' ? Number(form.payDay) : null;
      firstPayDayKind = PAY_DAY_KIND.DayOfMonth;
      secondPayDay = form.secondDay && form.secondDay !== 'last' ? Number(form.secondDay) : null;
      secondPayDayKind = form.secondDay === 'last' ? PAY_DAY_KIND.LastDayOfMonth : form.secondDay ? PAY_DAY_KIND.DayOfMonth : null;
    } else if (isMonthly) {
      firstPayDay = form.payDay && form.payDay !== 'last' ? Number(form.payDay) : null;
      firstPayDayKind = form.payDay === 'last' ? PAY_DAY_KIND.LastDayOfMonth : form.payDay ? PAY_DAY_KIND.DayOfMonth : null;
    } else if (isQuarterly) {
      firstPayDate = form.firstPayDate || null;
    }

    return {
      frequency: form.frequency || null,
      firstPayDay,
      firstPayDayKind,
      secondPayDay,
      secondPayDayKind,
      payPeriodStartDate: form.payPeriodStartDate || null,
      firstPayDate,
    };
  };

  /** Shared by saveAndContinue (setup mode) and commitEdits (admin-edit's Save Changes) — see
   * BasicInfoStep's own identical comment on this pattern. */
  const commit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    setShowErrors(true);
    if (Object.keys(validationErrors).length > 0) {
      throw new Error('validation failed');
    }

    setApiError(null);
    try {
      await saveIfValid();
      markStepComplete('payroll');
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not save your payroll setup. Please try again.');
      throw err;
    }
  };

  useImperativeHandle(ref, () => ({
    saveAndContinue: commit,
    commitEdits: commit,
    isDirty: () => JSON.stringify(form) !== JSON.stringify(baselineRef.current),
    async saveAndExit() {
      // Save and Exit never promotes to the canonical pay schedule, even when the schedule is already
      // fully valid — only Save and Continue does that, after its own validation. This always persists
      // Step 4's own module-owned draft instead, so whatever has been filled in so far survives a resume.
      setApiError(null);
      setNotice(null);
      try {
        await businessProfileSetupGateway.savePayScheduleDraft(toDraftRequest());
        setNotice('Your payroll setup progress was saved as a draft. You can continue anytime.');
      } catch (err) {
        setApiError(err instanceof ApiError ? err.message : 'Could not save your payroll setup. Please try again.');
      }
    },
  }));

  const err = (field: string) => (showErrors ? errors[field] : undefined);

  if (loading) return <p className="helper">Loading…</p>;

  return (
    <div>
      <div className="bp-grid">
        <div className="bp-form-group">
          <label htmlFor="bps-paySchedule">
            Pay Schedule <span className="req">*</span>
          </label>
          <select
            id="bps-paySchedule"
            className={`bp-select${err('frequency') ? ' invalid' : ''}`}
            value={form.frequency || ''}
            disabled={readOnly}
            onChange={(e) => setForm({ ...EMPTY, payPeriodStartDate: form.payPeriodStartDate, frequency: Number(e.target.value) as PayFrequency })}
          >
            <option value="">-- Select --</option>
            {FREQUENCY_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
          {err('frequency') && <div className="error">{err('frequency')}</div>}
        </div>
        <div className="bp-form-group">
          <label htmlFor="bps-payStart">
            Pay Period Start Date <span className="req">*</span>
          </label>
          <input
            id="bps-payStart"
            type="date"
            className={`bp-input${err('payPeriodStartDate') ? ' invalid' : ''}`}
            value={form.payPeriodStartDate}
            disabled={readOnly}
            onChange={(e) => set('payPeriodStartDate', e.target.value)}
          />
          {err('payPeriodStartDate') && <div className="error">{err('payPeriodStartDate')}</div>}
        </div>

        {!form.frequency && (
          <div className="bp-form-group">
            <label htmlFor="bps-payDay">Pay Day</label>
            <select id="bps-payDay" className="bp-select" disabled>
              <option>-- Select Pay Schedule First --</option>
            </select>
          </div>
        )}

        {isWeekly && (
          <div className="bp-form-group">
            <label htmlFor="bps-payDay">
              Pay Day <span className="req">*</span>
            </label>
            <select id="bps-payDay" className={`bp-select${err('payDay') ? ' invalid' : ''}`} value={form.payDay} disabled={readOnly} onChange={(e) => set('payDay', e.target.value)}>
              <option value="">-- Select Weekday --</option>
              {WEEKDAY_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
            {err('payDay') && <div className="error">{err('payDay')}</div>}
            <div className="helper">Choose the weekday employees are paid {form.frequency === PAY_FREQUENCY.Weekly ? 'each week' : 'every two weeks'}.</div>
          </div>
        )}

        {isSemi && (
          <>
            <div className="bp-form-group">
              <label htmlFor="bps-payDay">
                First Pay Day <span className="req">*</span>
              </label>
              <select id="bps-payDay" className={`bp-select${err('payDay') ? ' invalid' : ''}`} value={form.payDay} disabled={readOnly} onChange={(e) => set('payDay', e.target.value)}>
                <option value="">-- Select 1st–15th --</option>
                {monthDayOptions(1, 15, false).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {err('payDay') && <div className="error">{err('payDay')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-secondDay">
                Second Pay Day <span className="req">*</span>
              </label>
              <select id="bps-secondDay" className={`bp-select${err('secondDay') ? ' invalid' : ''}`} value={form.secondDay} disabled={readOnly} onChange={(e) => set('secondDay', e.target.value)}>
                <option value="">-- Select 16th–Last Day --</option>
                {monthDayOptions(16, 28, true).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {err('secondDay') && <div className="error">{err('secondDay')}</div>}
            </div>
          </>
        )}

        {isMonthly && (
          <div className="bp-form-group">
            <label htmlFor="bps-payDay">
              Pay Day <span className="req">*</span>
            </label>
            <select id="bps-payDay" className={`bp-select${err('payDay') ? ' invalid' : ''}`} value={form.payDay} disabled={readOnly} onChange={(e) => set('payDay', e.target.value)}>
              <option value="">-- Select Day --</option>
              {monthDayOptions(1, 28, true).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {err('payDay') && <div className="error">{err('payDay')}</div>}
            <div className="helper">The Last Day of Month option automatically adjusts for February and 30/31-day months.</div>
          </div>
        )}

        {isQuarterly && (
          <div className="bp-form-group">
            <label htmlFor="bps-quarterlyPayDate">
              First Pay Date <span className="req">*</span>
            </label>
            <input
              id="bps-quarterlyPayDate"
              type="date"
              className={`bp-input${err('firstPayDate') ? ' invalid' : ''}`}
              value={form.firstPayDate}
              min={form.payPeriodStartDate || undefined}
              disabled={readOnly}
              onChange={(e) => set('firstPayDate', e.target.value)}
            />
            {err('firstPayDate') && <div className="error">{err('firstPayDate')}</div>}
            <div className="helper">Subsequent quarterly pay dates recur every 3 months from this date.</div>
          </div>
        )}
      </div>

      {notice && <div className="notice">{notice}</div>}
      {apiError && <div className="notice" style={{ color: 'var(--bps-danger)' }}>{apiError}</div>}
    </div>
  );
});
